/**
 * StatusBadge - status pill for tenant/user status and role values
 */

import type { TenantStatus, UserStatus, UserRole, ProjectStatus } from '@resolve-ai/shared';
import { TenantStatusLabels } from '@resolve-ai/shared';

type StatusVariant = TenantStatus | UserStatus | UserRole | ProjectStatus;

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getStatusColor(status: StatusVariant): string {
  if (status === 'active' || status === 'admin') {
    return 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400';
  }
  if (status === 'suspended') {
    return 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400';
  }
  if (status === 'pending') {
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-400';
  }
  return 'bg-gray-100 text-[#444444] dark:bg-gray-700 dark:text-gray-300';
}

function getDotColor(status: StatusVariant): string {
  if (status === 'active' || status === 'admin') return 'bg-green-500';
  if (status === 'suspended') return 'bg-red-600';
  if (status === 'pending') return 'bg-yellow-500';
  return 'bg-gray-400';
}

interface StatusBadgeProps {
  status: StatusVariant;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  showDot?: boolean;
}

export function StatusBadge({ status, label, size = 'md', showDot = true }: StatusBadgeProps) {
  const sizeClasses = {
    sm: 'px-2 py-0.5',
    md: 'px-2 py-1',
    lg: 'p-2',
  };

  const displayLabel = label ?? TenantStatusLabels[status as TenantStatus] ?? capitalize(status);

  return (
    <p className={`${sizeClasses[size]} w-max flex gap-[0.38rem] items-center rounded-lg ${getStatusColor(status)}`}>
      {showDot && <span className={`rounded-full w-1.5 h-1.5 ${getDotColor(status)}`} />}
      <span className="font-caption">{displayLabel}</span>
    </p>
  );
}
